import React, { useState } from 'react';
import { Box, Group, Modal, Tooltip, ActionIcon } from '@mantine/core';
import { IconMaximize } from '@tabler/icons-react';

import { ScimAceEditor } from './ScimAceEditor';

const fullScreenModalStyles = {
    content: { height: '95vh', maxHeight: '95vh', display: 'flex', flexDirection: 'column' as const },
    body: {
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column' as const,
        padding: 'var(--mantine-spacing-md)',
    },
    header: { padding: 'var(--mantine-spacing-md)' },
};

export interface SqlEditorWithFullscreenProps {
    value: string;
    onChange?: (v: string) => void;
    readOnly: boolean;
    /** Distinct Ace `name` / modal identity (e.g. attribute name). */
    fieldName: string;
    /** Modal title; defaults to "SQL". */
    modalTitle?: string;
    inlineHeight?: string;
    /**
     * When set, the full-screen control is placed on the same row (e.g. with the field label).
     * The editor is rendered full-width below. When omitted, a compact row is used (icon top-aligned with the editor).
     */
    labelRow?: React.ReactNode;
}

/**
 * Inline SQL Ace editor plus a full-screen modal (no hash / deep link), matching the layout
 * used for Body full-screen in EntityDetailDialog.
 */
export const SqlEditorWithFullscreen: React.FC<SqlEditorWithFullscreenProps> = ({
    value,
    onChange,
    readOnly,
    fieldName,
    modalTitle = 'SQL',
    inlineHeight = '200px',
    labelRow,
}) => {
    const [fullOpen, setFullOpen] = useState(false);

    const fullScreenButton = (
        <Tooltip label="Full screen" withArrow>
            <Box flex="0 0 auto">
                <ActionIcon
                    variant="subtle"
                    size="lg"
                    aria-label="Open SQL in full screen"
                    onClick={() => setFullOpen(true)}
                >
                    <IconMaximize size={18} />
                </ActionIcon>
            </Box>
        </Tooltip>
    );

    return (
        <>
            {labelRow != null ? (
                <>
                    <Group justify="space-between" align="center" gap="sm" wrap="nowrap" mb={4} w="100%">
                        <Box flex={1} miw={0}>{labelRow}</Box>
                        {fullScreenButton}
                    </Group>
                    <ScimAceEditor
                        mode="sql"
                        value={value}
                        onChange={readOnly ? undefined : onChange}
                        readOnly={readOnly}
                        height={inlineHeight}
                        name={`sql-inline-${fieldName}`}
                        highlightActiveLine={!readOnly}
                    />
                </>
            ) : (
                <Group wrap="nowrap" gap="xs" align="flex-start" w="100%">
                    <Box flex={1} miw={0}>
                        <ScimAceEditor
                            mode="sql"
                            value={value}
                            onChange={readOnly ? undefined : onChange}
                            readOnly={readOnly}
                            height={inlineHeight}
                            name={`sql-inline-${fieldName}`}
                            highlightActiveLine={!readOnly}
                        />
                    </Box>
                    {fullScreenButton}
                </Group>
            )}
            <Modal
                opened={fullOpen}
                onClose={() => setFullOpen(false)}
                size="100vw"
                title={modalTitle}
                styles={fullScreenModalStyles}
            >
                <Box flex={1} style={{ minHeight: 0 }}>
                    <ScimAceEditor
                        mode="sql"
                        value={value}
                        onChange={readOnly ? undefined : onChange}
                        readOnly={readOnly}
                        height="calc(95vh - 140px)"
                        width="100%"
                        name={`sql-fullscreen-${fieldName}`}
                        highlightActiveLine={!readOnly}
                    />
                </Box>
            </Modal>
        </>
    );
};
